import React, { useState, useRef, useEffect } from 'react';
import { useMentionDetection } from '../../hooks/useMentionDetection';
import UserMentionAutocomplete from './UserMentionAutocomplete/UserMentionAutocomplete';
import { MentionMetadata } from 'shared/types/contentTypes';

interface NewCommentFormProps {
  onSubmit: (text: string, mentions?: MentionMetadata[]) => Promise<void>;
  onCancel?: () => void;
  placeholder?: string;
  buttonText?: string;
  autoFocus?: boolean;
  enableMentions?: boolean; // Opt-in for @ mention functionality
}

const NewCommentForm: React.FC<NewCommentFormProps> = ({
  onSubmit,
  onCancel,
  placeholder = 'Share your perspective...',
  buttonText = 'Add',
  autoFocus = false,
  enableMentions = true, // Enabled by default
}) => {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionMetadata, setMentionMetadata] = useState<MentionMetadata[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Auto-resize on mount and text change
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [text]);

  // Mention detection hook
  const mentionDetection = useMentionDetection({
    text,
    cursorPosition,
    onMentionInsert: (userId, displayName) => {
      // Insert the mention at the cursor position
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;

        // Find the @ symbol position
        let atPosition = start;
        for (let i = start - 1; i >= 0; i--) {
          if (text[i] === '@') {
            atPosition = i;
            break;
          }
        }

        // Insert full display name WITH SPACES preserved
        const mentionText = `@${displayName}`;
        const beforeMention = text.slice(0, atPosition);
        const afterMention = text.slice(start);
        const newText = `${beforeMention}${mentionText} ${afterMention}`;

        setText(newText);

        // Store mention metadata
        const newMention: MentionMetadata = {
          userId,
          displayName,
          startIndex: atPosition,
          endIndex: atPosition + mentionText.length
        };
        setMentionMetadata(prev => [...prev, newMention]);

        // Set cursor position after the mention
        const newCursorPos = atPosition + mentionText.length + 1; // +1 for space after
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
          setCursorPosition(newCursorPos);
          textarea.focus();
        }, 0);
      }
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!text.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(text, mentionMetadata);
      setText(''); // Clear input after successful submission
      setMentionMetadata([]); // Clear mention metadata
      setIsFocused(false); // Remove focus after submission
    } catch (error) {
      console.error('Failed to submit perspective:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle text change and cursor position tracking
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  const handleCursorMove = () => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  };

  // Only show the submit button when the textarea is focused or has content
  const showButtons = isFocused || text.trim().length > 0;
  
  return (
    <div className="px-3 mb-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Text input */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onSelect={handleCursorMove}
            onClick={handleCursorMove}
            onKeyUp={handleCursorMove}
            onFocus={() => setIsFocused(true)}
            onKeyDown={(e) => {
              // Handle mention autocomplete navigation
              if (enableMentions && mentionDetection.showAutocomplete) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  mentionDetection.navigateDown();
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  mentionDetection.navigateUp();
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const selectedSuggestion = mentionDetection.suggestions[mentionDetection.selectedIndex];
                  if (selectedSuggestion) {
                    mentionDetection.selectSuggestion(selectedSuggestion);
                  }
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  mentionDetection.dismissAutocomplete();
                  return;
                }
              }

              // Submit on Enter without Shift key (only if autocomplete is not open)
              if (e.key === 'Enter' && !e.shiftKey) {
                if (!mentionDetection.showAutocomplete) {
                  e.preventDefault();
                  if (text.trim()) {
                    handleSubmit(e);
                  }
                }
              }
            }}
            onBlur={(e) => {
              // Only unfocus if not submitting and clicking away from the form
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                // Don't unfocus if there's text
                if (text.trim().length === 0) {
                  setIsFocused(false);
                  // Auto-cancel when losing focus on empty form
                  if (onCancel) {
                    onCancel();
                  }
                }
              }
            }}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="w-full font-light px-4 py-3 border border-gray-200/50 rounded-xl overflow-hidden min-h-[80px] text-base focus:outline-none focus:ring-2 focus:ring-coral-pink/20 focus:border-coral-pink/30 bg-white placeholder-gray-400 transition-all duration-200"
            rows={3}
            style={{ resize: 'none' }}
            data-testid="comment-input"
          />

          {/* Mention Autocomplete */}
          {enableMentions && (
            <UserMentionAutocomplete
              isOpen={mentionDetection.showAutocomplete}
              suggestions={mentionDetection.suggestions}
              isLoading={mentionDetection.isLoading}
              selectedIndex={mentionDetection.selectedIndex}
              onSelect={mentionDetection.selectSuggestion}
            />
          )}

          {isFocused && !mentionDetection.showAutocomplete && (
            <span className="absolute right-4 bottom-3 text-xs font-light text-gray-400 pointer-events-none hidden xl:block">
              Press Enter to submit, Shift+Enter for new line
            </span>
          )}
          {isFocused && !mentionDetection.showAutocomplete && (
            <span className="absolute right-3 bottom-3 text-[11px] font-light text-gray-400 pointer-events-none text-center xl:hidden">
              Enter to Submit
            </span>
          )}
        </div>
        
        {/* Submit and cancel buttons - only show when focused or has content */}
        {showButtons && (
          <div className="flex justify-end gap-3 animate-fadeIn">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                Cancel
              </button>
            )}
            
            <button
              type="submit"
              disabled={!text.trim() || isSubmitting}
              className={`px-6 py-2 text-sm font-medium rounded-full transition-all duration-300 shadow-sm ${
                !text.trim() || isSubmitting
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-coral-pink to-coral-orange text-white hover:shadow-lg hover:scale-105'
              }`}
              data-testid="comment-submit-button"
            >
              {isSubmitting ? 'Adding...' : buttonText}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default NewCommentForm;